import { useCallback, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import type {
    FlexProps,
    DrawerProps,
    ComponentEventHandler,
} from '@archipro-design/aria';
import { Caption } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Drawer, Flex } from '@archipro-design/aria';
import { useFilterStates } from '@modules/search/hook/use-filter-states';
import type {
    ElasticSearchCategories,
    ElasticSearchSortOptions,
} from '../../type';
import type { FilterStates, FilterStatesKey } from '../../type/filter-state';
import {
    getAppliedFilterNumber,
    hasAppliedFilter,
} from '../../util/filter-state-util';
import { resetFilterStates } from '../../util/reset-filter-states';
import type { AccordionPanel } from './FilterAccordion';
import FilterAccordion from './FilterAccordion';
import FilterContainer from './FilterContainer';
import type { FilterType } from './FilterFactory';
import FilterFactory from './FilterFactory';
import type { SortOption } from '../../hook/use-search-sorts';
import { useSearchSorts } from '../../hook/use-search-sorts';
import { pluralize, capitalizeFirstLetter } from '~/utils';
import { CATEGORY_SELECTOR_MOBILE_ARGS } from '@modules/search/components/filter-drawer/FilterCategorySelector';
import type { CategoryFilterState } from '@modules/search/components/filter-drawer/FilterCategorySelector';
import { getAppliedFilterItems } from '~/modules/search/util/get-applied-filter-items';
import type { ViewProjectsByOption } from '~/modules/search/hook/use-view-projects-by';
import { useViewProjectsBy } from '~/modules/search/hook/use-view-projects-by';
import { VIEW_PROJECTS_BY_OPTIONS } from '~/modules/search/hook/use-view-projects-by';
import { useCurrentLocation } from '~/modules/location/hook/use-current-location';
import { useWindowSize } from '~/modules/root/hook/useWindowSize';
import { createFacetFilterSections } from '../../util/create-facet-filters';
import { hasFeature, useAppData } from '@modules/root';
import { useTracker } from '@archipro-website/tracker';

export type FilterSectionFilterType = FilterGroup | FilterType;

interface FilterGroup {
    kind: 'FilterGroup';
    key: string;
    gap?: FlexProps['gap'];
    filters: FilterSectionFilterType[];
}

export interface FilterSection {
    title: string;
    key?: string;
    filter: FilterSectionFilterType;
    activePanel?: boolean;
    adminOnly?: boolean;
}

interface FilterDrawerProps extends Pick<DrawerProps, 'open'> {
    filterSections: FilterSection[];
    filterStates: FilterStates;
    category?: ElasticSearchCategories;
    onOpenChange: ComponentEventHandler<boolean>;
    onApplyFilters?: ComponentEventHandler<FilterStates>;
    ignoreFilterKeys?: FilterStatesKey[];
    hasMarketingView?: boolean;
}

const FilterDrawer = ({
    filterSections,
    filterStates: initialStates,
    category,
    open,
    onOpenChange,
    onApplyFilters,
    ignoreFilterKeys,
    hasMarketingView = false,
}: FilterDrawerProps) => {
    const { desktop } = useAppDisplayModeContext();
    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const {
        loading: aggregateLoading,
        filterStates,
        updateFilter,
        resetFilters,
        aggregate,
    } = useFilterStates({ initialStates, category });

    useUpdateEffect(() => {
        // reset filter states to initial states when open the drawer
        if (open) resetFilters(initialStates);
    }, [open]);

    const appliedNum = filterStates
        ? getAppliedFilterNumber(filterStates, ignoreFilterKeys)
        : 0;

    const { sortOptions: initialSortOptions } = useSearchSorts({ category });
    const [sortOptions, setSortOptions] =
        useState<SortOption[]>(initialSortOptions);
    useUpdateEffect(
        () => setSortOptions(initialSortOptions),
        [initialSortOptions]
    );

    const {
        currentLocationDisabled,
        currentLocation,
        getCurrentLocation,
        loading: currentLocationLoading,
    } = useCurrentLocation();

    const loading = currentLocationLoading || aggregateLoading;

    const { viewOptions: initialViewProjectsByOptions } = useViewProjectsBy();
    const [viewProjectsByOptions, setViewProjectsByOptions] = useState<
        ViewProjectsByOption[]
    >(initialViewProjectsByOptions);

    const renderFilter = useCallback(
        (filter: FilterSectionFilterType): JSX.Element | null => {
            if (!filterStates) return null;
            if (!enableAwardBadges && filter.kind === 'AwardsFilter') {
                return null;
            }
            if (filter.kind === 'FilterGroup') {
                const childFilters = filter.filters.reduce((previous, f) => {
                    const childFilter = renderFilter(f);
                    if (childFilter) previous.push(childFilter);
                    return previous;
                }, [] as JSX.Element[]);

                if (!childFilters.length) return null;

                return (
                    <Flex
                        column
                        gap={filter.gap}
                        key={filter.key}
                        className={'filter-' + filter.key}
                    >
                        {childFilters}
                    </Flex>
                );
            }

            const state =
                filter.filterKey === 'facetOptions'
                    ? filterStates[filter.filterKey]?.find(
                          (subState) => subState.id === filter.subFilterKey
                      )?.state
                    : filterStates[filter.filterKey];
            if (
                !state ||
                typeof state === 'number' ||
                Array.isArray(state) ||
                (ignoreFilterKeys &&
                    ignoreFilterKeys.includes(filter.filterKey))
            )
                return null;

            const shouldOverrideRootLabel =
                !desktop &&
                filter.kind === 'CategorySelector' &&
                state &&
                category;

            if (shouldOverrideRootLabel) {
                const categorySelectorState = state as CategoryFilterState;
                categorySelectorState.category.name = capitalizeFirstLetter(
                    category.toString()
                );

                return FilterFactory({
                    filterStates,
                    filter,
                    state: categorySelectorState,
                    updateFilter,
                    currentLocationDisabled,
                    currentLocation,
                    getCurrentLocation,
                    loading,
                    aggregate,
                });
            }

            return FilterFactory({
                filterStates,
                filter,
                state,
                updateFilter,
                currentLocationDisabled,
                currentLocation,
                getCurrentLocation,
                loading,
                aggregate,
            });
        },
        [
            filterStates,
            ignoreFilterKeys,
            desktop,
            category,
            updateFilter,
            currentLocationDisabled,
            currentLocation,
            getCurrentLocation,
            aggregate,
            loading,
            enableAwardBadges,
        ]
    );

    // insert category selector in accordion
    const sections = filterSections ? filterSections.slice() : [];
    if (!desktop && category && filterStates?.category) {
        const categoryName = pluralize(
            category !== 'product' ? 2 : 1,
            capitalizeFirstLetter(category),
            true
        );

        const categorySection: FilterSection = {
            title: `${categoryName} category`,
            filter: {
                kind: 'CategorySelector',
                filterKey: 'category',
                props: {
                    category: filterStates.category.category,
                    ...CATEGORY_SELECTOR_MOBILE_ARGS,
                },
            },
        };

        sections.unshift(categorySection);
    }

    const facetSections = filterStates.facetOptions
        ? createFacetFilterSections(filterStates.facetOptions)
        : [];

    const panels = [...sections, ...facetSections].reduce(
        (availablePanels, currentSection) => {
            if (currentSection.adminOnly && !hasMarketingView) {
                return availablePanels;
            }
            const { title, filter, activePanel, key } = currentSection;
            const content = renderFilter(filter);
            if (!content) return availablePanels;
            const applied = hasAppliedFilter(
                filterStates,
                getFilterKeys(filter)
            );
            const appliedFilters = getAppliedFilterItems(
                [currentSection],
                filterStates,
                updateFilter,
                ignoreFilterKeys
            );
            availablePanels.push({
                title: { title, applied, appliedFilters, key: key ?? title },
                content,
                className: `filter-section-${key ?? title}`,
                activePanel: activePanel,
            });
            return availablePanels;
        },
        [] as AccordionPanel[]
    );

    const showFilterByTitle =
        !desktop && (filterStates?.category || panels.length > 0);

    const viewResultBtnText = desktop
        ? applyCount('View results', filterStates?.totalCount ?? 0, true)
        : applyCount(
              'VIEW',
              filterStates?.totalCount ?? 0,
              true,
              pluralize(
                  filterStates?.totalCount ?? 0,
                  [' RESULT', ' RESULTS'],
                  true
              )
          );

    const { height: windowHeight } = useWindowSize();
    const tracker = useTracker();

    const handleTrackSort = () => {
        const sortBy = sortOptions.find((o) => o.selected);
        if (sortBy) {
            tracker.log('SortClick', {
                url: new URL(window.location.href),
                data: {
                    ExtraData: {
                        sortBy: sortBy?.header,
                    },
                },
            });
        }
    };

    return (
        <Drawer
            open={open}
            styles={{
                height: desktop
                    ? '-webkit-fill-available'
                    : `${windowHeight}px`,
            }}
            fluid={true}
            onOpenChange={(e, data) => onOpenChange(e, !!data?.open)}
            autoDisableBodyScroll={desktop}
            content={
                <FilterContainer
                    open={open}
                    secondaryButton={{
                        children: applyCount('Clear all filters', appliedNum),
                        onClick: () => {
                            if (!filterStates) return;
                            resetFilters(
                                resetFilterStates(
                                    filterStates,
                                    ignoreFilterKeys
                                )
                            );
                        },
                    }}
                    primaryButton={{
                        children: viewResultBtnText,
                        onClick: (e) => {
                            // find selected sort option,
                            // if the selected option is the first one, don't add the sort to the url
                            // we use the first option as default sort
                            const selectSort = sortOptions.find(
                                (o, index) => o.selected && !!index
                            );
                            filterStates.sortBy = selectSort?.value;
                            handleTrackSort();
                            onApplyFilters?.(e, filterStates);
                        },
                        loading,
                    }}
                    onClose={(e) => onOpenChange(e, false)}
                    category={category}
                    sortOptions={sortOptions}
                    onSortChange={(sort: ElasticSearchSortOptions) => {
                        const newOptions = [...sortOptions];
                        newOptions.forEach((o) => {
                            o.selected = o.value === sort;
                        });
                        setSortOptions(newOptions);
                    }}
                    viewProjectsByOptions={viewProjectsByOptions}
                    onViewProjectsByChange={(value: string) => {
                        const selected = VIEW_PROJECTS_BY_OPTIONS.find(
                            (option) => option.param === value
                        );
                        if (!selected) return;

                        const selectedItem =
                            filterStates.viewProjectsBy?.items.find(
                                ({ value }) => value === selected.param
                            );

                        resetFilters({
                            ...filterStates,
                            viewProjectsBy: {
                                items: filterStates.viewProjectsBy?.items ?? [],
                                kind: 'RadioGroup',
                                selectedItem,
                            },
                        });
                        const newOptions = [...viewProjectsByOptions];
                        newOptions.forEach((o) => {
                            o.selected = o.value === value;
                        });
                        setViewProjectsByOptions(newOptions);
                    }}
                >
                    {showFilterByTitle && (
                        <Caption variant="03" className="filter-by-title">
                            {`FILTER BY`}
                        </Caption>
                    )}

                    {desktop &&
                        filterStates &&
                        filterStates.category &&
                        renderFilter({
                            kind: 'CategorySelector',
                            filterKey: 'category',
                            props: { category: filterStates.category.category },
                        })}

                    {panels.length > 0 && <FilterAccordion panels={panels} />}
                </FilterContainer>
            }
        />
    );
};

const getFilterKeys = (
    filter: FilterSectionFilterType,
    filterKeys?: FilterStatesKey[]
): FilterStatesKey[] => {
    const currentKeys = filterKeys ?? [];
    if (filter.kind !== 'FilterGroup') {
        currentKeys.push(filter.filterKey);
    } else {
        filter.filters.forEach((f) => getFilterKeys(f, currentKeys));
    }
    return currentKeys as FilterStatesKey[];
};

export const applyCount = (
    label: string,
    count: number,
    displayZero = false,
    afterLabel = ''
): string => {
    let countStr = count || displayZero ? `(${count.toLocaleString()})` : '';

    if (afterLabel.length > 0) {
        countStr = count || displayZero ? count.toLocaleString() : '';
    }
    return `${label} ${countStr}${afterLabel}`;
};

export default FilterDrawer;
